import { DataGlueGraph, DataConnection, NodeInstance } from '@glueapp/graph-types';
import { ValidationRule, ValidationRuleResult, ValidationError } from '../ValidationRule';

export const ConnectionIntegrityRule: ValidationRule = (graph: DataGlueGraph): ValidationRuleResult => {
    const errors: ValidationError[] = [];

    // Check main graph connections
    const mainGraphErrors = validateConnections(graph.connections, graph.nodeInstances, 'main');
    errors.push(...mainGraphErrors);

    // Check each function's connections
    if (graph.functions) {
        for (const func of graph.functions) {
            const functionErrors = validateConnections(func.connections, func.nodeInstances, func.id);
            errors.push(...functionErrors);
        }
    }

    return {
        valid: errors.length === 0,
        errors,
    };
};

function validateConnections(connections: DataConnection[], nodeInstances: NodeInstance[], context: string): ValidationError[] {
    const errors: ValidationError[] = [];
    const nodeMap = new Map(nodeInstances.map(node => [node.id, node]));

    for (const connection of connections) {
        // Validate source node exists
        const sourceNode = nodeMap.get(connection.sendingNodeInstanceId);
        if (!sourceNode) {
            const location =
                context === 'main'
                    ? { context: 'main graph', connectionId: connection.id }
                    : { functionId: context, connectionId: connection.id };

            errors.push({
                code: 'MISSING_SOURCE_NODE',
                message: `Connection source node ${connection.sendingNodeInstanceId} not found`,
                severity: 'error',
                location,
            });
        }

        // Validate target node exists
        const targetNode = nodeMap.get(connection.receivingNodeInstanceId);
        if (!targetNode) {
            const location =
                context === 'main'
                    ? { context: 'main graph', connectionId: connection.id }
                    : { functionId: context, connectionId: connection.id };

            errors.push({
                code: 'MISSING_TARGET_NODE',
                message: `Connection target node ${connection.receivingNodeInstanceId} not found`,
                severity: 'error',
                location,
            });
        }

        // Only validate ports if nodes exist
        if (sourceNode) {
            // Validate source port exists
            if (!validateSourcePort(sourceNode, connection)) {
                const location =
                    context === 'main'
                        ? { context: 'main graph', connectionId: connection.id, nodeId: connection.sendingNodeInstanceId }
                        : { functionId: context, connectionId: connection.id, nodeId: connection.sendingNodeInstanceId };

                errors.push({
                    code: 'MISSING_SOURCE_PORT',
                    message: `Source port '${connection.sendingPortName}' not found on node ${sourceNode.nodeId} (instance ${sourceNode.id})`,
                    severity: 'error',
                    location,
                });
            }
        }

        if (targetNode) {
            // Validate target port exists
            if (!validateTargetPort(targetNode, connection)) {
                const location =
                    context === 'main'
                        ? { context: 'main graph', connectionId: connection.id, nodeId: connection.receivingNodeInstanceId }
                        : { functionId: context, connectionId: connection.id, nodeId: connection.receivingNodeInstanceId };

                errors.push({
                    code: 'MISSING_TARGET_PORT',
                    message: `Target port '${connection.receivingPortName}' not found on node ${targetNode.nodeId} (instance ${targetNode.id})`,
                    severity: 'error',
                    location,
                });
            }
        }
    }

    return errors;
}

function validateSourcePort(sourceNode: NodeInstance, connection: DataConnection): boolean {
    // For inherent connections, check for standard inherent ports
    if (connection.sendingPortType === 'inherent') {
        return connection.sendingPortName === 'InherentOut';
    }

    // For external connections, check in the node instance's output ports
    return sourceNode.outPorts.some(port => port.name === connection.sendingPortName);
}

function validateTargetPort(targetNode: NodeInstance, connection: DataConnection): boolean {
    // For inherent receiving ports, check for standard inherent ports
    // Any port can connect to inherent in ports (for triggering execution)
    if (connection.receivingPortType === 'inherent') {
        return connection.receivingPortName === 'InherentIn';
    }

    // For external receiving ports, only allow external-to-external connections
    // Inherent out ports cannot connect to external ports (they don't carry meaningful data)
    if (connection.sendingPortType === 'inherent') {
        return false;
    }

    // For external-to-external connections, check in the node instance's input ports
    return targetNode.inPorts.some(port => port.name === connection.receivingPortName);
}
